import type { EntryCollection } from "@/types/entryCollection";

/**
 * Format a timer (stopwatch) duration in milliseconds. Under an hour:
 * `mm:ss.SS` (centisecond precision), e.g. 150000 → "02:30.00". At or over an
 * hour: `H:MM:SS` — centiseconds are dropped (noise at that scale) and hours are
 * unpadded, e.g. 3661230 → "1:01:01". Mirrors iOS `TimerStateMachine.format`.
 * Returns an empty string for non-finite/negative input (e.g. a running sample
 * that has no final elapsedMs yet) so it renders as in-progress/blank.
 */
export function formatTimerDuration(elapsedMs: number | null | undefined): string {
  if (elapsedMs == null || !Number.isFinite(elapsedMs) || elapsedMs < 0) return "";
  // Truncate (floor) the centisecond component to match iOS, which uses integer
  // division (`ms / 10`) in TimerStateMachine.format. Rounding would overstate
  // the captured duration and round up across boundaries (59,999 ms must render
  // as 00:59.99, not 01:00.00).
  const totalCentiseconds = Math.floor(elapsedMs / 10);
  const centiseconds = totalCentiseconds % 100;
  const totalSeconds = Math.floor(totalCentiseconds / 100);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);
  const pad = (value: number) => String(value).padStart(2, "0");
  // At/over an hour switch to H:MM:SS (drop centiseconds); otherwise mm:ss.SS.
  if (hours >= 1) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}.${pad(centiseconds)}`;
}

/**
 * Convert a timer's elapsed milliseconds to a number of seconds for CSV export.
 * Lossless — keeps full millisecond precision (e.g. 185340 → 185.34,
 * 185345 → 185.345) rather than truncating to the centiseconds the UI displays,
 * so the export stays the source of truth and never silently drops recorded ms.
 * Returns a number (not a formatted string) so the exported column remains
 * numeric for downstream stats. The CSV labels the unit by appending "(s)" to
 * the column header / Trait Name; callers guard idle/running samples (no final
 * value) before calling this.
 */
export function timerMsToSeconds(elapsedMs: number): number {
  return elapsedMs / 1_000;
}

/**
 * Resolve a timer sample's final stopped duration in ms, or null if it has no
 * value yet. Per the shared schema (see TimerEntry), a timer is "stopped" ONLY
 * when `elapsedMs > 0`; an idle slot (elapsedMs absent or persisted as 0) and a
 * running slot (startedAtEpochMs set, elapsedMs absent) both have no final value
 * and must surface as blank. Use this everywhere a timer value is read so the
 * idle/running/stopped distinction stays consistent across display and export.
 */
export function getStoppedTimerMs(
  entry: { elapsedMs?: number; startedAtEpochMs?: number } | null | undefined
): number | null {
  if (entry == null) return null;
  // Running takes precedence over stopped (matches iOS TimerStateMachine.state,
  // which checks startedAtEpochMs first): a non-null startedAtEpochMs means the
  // sample is still counting and has no final value yet — even if a stale
  // elapsedMs lingers from a prior run.
  if (entry.startedAtEpochMs != null) return null;
  return entry.elapsedMs != null && entry.elapsedMs > 0 ? entry.elapsedMs : null;
}

type TimerEntryTimed = {
  elapsedMs?: number;
  startedAtEpochMs?: number;
  updatedAt?: TimerTimestamp;
  createdAt?: TimerTimestamp;
};

type TimerTimestamp = { seconds?: number; nanoseconds?: number };

interface TimerCandidate {
  entry: TimerEntryTimed | undefined;
  fallbackTimestamp: TimerTimestamp | undefined;
  stableId: string;
}

/**
 * Core ranking shared by every "which timer value wins" path: from a set of
 * (entry, fallbackTimestamp, stableId) candidates, return the stopped duration
 * with the latest timestamp, ranked by entry `updatedAt` → entry `createdAt` →
 * `fallbackTimestamp` (the collection's createdAt), each compared at full
 * seconds+nanoseconds precision. Ties at that precision fall back to `stableId`
 * (collection id + sample key) so the result is deterministic rather than
 * depending on object/array iteration order. Centralizing this means the
 * heatmap display and the CSV export cannot drift apart on ordering.
 */
function pickMostRecentStoppedTimerMs(
  candidates: TimerCandidate[]
): number | null {
  let bestMs: number | null = null;
  let bestTimestamp: readonly [number, number] = [-Infinity, 0];
  let bestStableId = "";
  for (const { entry, fallbackTimestamp, stableId } of candidates) {
    const elapsedMs = getStoppedTimerMs(entry);
    if (elapsedMs == null) continue;
    const timestamp = timestampParts(entry?.updatedAt, entry?.createdAt, fallbackTimestamp);
    if (isLaterTimerCandidate(timestamp, stableId, bestTimestamp, bestStableId)) {
      bestTimestamp = timestamp;
      bestStableId = stableId;
      bestMs = elapsedMs;
    }
  }
  return bestMs;
}

function timestampParts(...timestamps: Array<TimerTimestamp | undefined>): readonly [number, number] {
  for (const timestamp of timestamps) {
    if (typeof timestamp?.seconds === "number" && Number.isFinite(timestamp.seconds)) {
      const nanoseconds =
        typeof timestamp.nanoseconds === "number" && Number.isFinite(timestamp.nanoseconds) ? timestamp.nanoseconds : 0;
      return [timestamp.seconds, nanoseconds];
    }
  }
  return [0, 0];
}

function isLaterTimerCandidate(
  timestamp: readonly [number, number],
  stableId: string,
  bestTimestamp: readonly [number, number],
  bestStableId: string
): boolean {
  if (timestamp[0] !== bestTimestamp[0]) return timestamp[0] > bestTimestamp[0];
  if (timestamp[1] !== bestTimestamp[1]) return timestamp[1] > bestTimestamp[1];
  return stableId > bestStableId;
}

function collectionSortKey(collection: EntryCollection): string {
  if (collection.id) return collection.id;
  const timestamp = timestampParts(collection.createdAt);
  return `missing:${collection.createdBy?.id ?? ""}:${timestamp[0]}:${timestamp[1]}`;
}

/**
 * Pick the single most-recent stopped timer duration (ms) across all given
 * collections and all their samples, ranked by the sample's `updatedAt` (then
 * `createdAt`, then the collection's `createdAt`). Returns null if nothing is
 * stopped. This is the source of truth for "the one timer value to show for a
 * plot/trait" (heatmap cell, plot-map value):
 *  - scanning every collection means a newer idle/running collection cannot hide
 *    a stopped duration recorded in an older one, and
 *  - ranking by timestamp means a later-stopped sample wins over an earlier one
 *    rather than just taking the lowest sample key.
 */
export function getMostRecentStoppedTimerMs(collections: EntryCollection[]): number | null {
  return pickMostRecentStoppedTimerMs(
    collections.flatMap((collection) => {
      const fallbackTimestamp = collection.createdAt;
      const collectionId = collectionSortKey(collection);
      return Object.entries(collection.timerEntries ?? {}).map(([sampleKey, entry]) => ({
        entry,
        fallbackTimestamp,
        stableId: `${collectionId}|timer|${sampleKey}|measurement:0`,
      }));
    })
  );
}

/**
 * Like {@link getMostRecentStoppedTimerMs} but scoped to ONE sample key across
 * collections — the per-sample value for the wide CSV export. Uses the same
 * entry-timestamp ranking so a per-sample CSV column agrees with the heatmap even
 * when a sample is edited/re-stopped in place in an older collection.
 */
export function getMostRecentStoppedTimerMsForSample(collections: EntryCollection[], sampleKey: string): number | null {
  return pickMostRecentStoppedTimerMs(
    collections.map((collection) => ({
      entry: collection.timerEntries?.[sampleKey],
      fallbackTimestamp: collection.createdAt,
      stableId: `${collectionSortKey(collection)}|timer|${sampleKey}|measurement:0`,
    }))
  );
}
